/**
 * The Value Realisation Index™ mark.
 *
 * Concept: five ascending strokes on a navy field.
 *   1. Clarity           – the shortest, the foundation
 *   2. Alignment         – slightly taller
 *   3. Execution         – halfway up
 *   4. Value Realisation – the tallest of the linen four
 *   5. Choice            – rendered in orange, tallest, slightly leaning,
 *                          topped by a dot — the discipline that emerges freely.
 *
 * Props
 * ─────
 * • size          – rendered width in px (default 48). When `withWordmark`
 *                   is true the aspect is 1:1 (60×60); otherwise the mark
 *                   alone renders at 48×48.
 * • title         – accessible title
 * • palette       – 'default' | 'inverse' | 'mono-navy' | 'mono-linen'
 * • withWordmark  – if true, includes 'Value Realisation' in orange italic
 *                   below the baseline. Off in the navbar (text already sits
 *                   beside the mark); on in shareable exports.
 */
export default function VriMark({
  size = 48,
  title = "The Value Realisation Index",
  palette = "default",
  withWordmark = false,
  className = "",
}) {
  const p =
    {
      default: { bg: "#0E2148", stroke: "#F4EEDC", accent: "#E86A20", rule: "#E86A20", word: "#E86A20" },
      inverse: { bg: "#F4EEDC", stroke: "#0E2148", accent: "#E86A20", rule: "#E86A20", word: "#E86A20" },
      "mono-navy": { bg: "#F4EEDC", stroke: "#0E2148", accent: "#0E2148", rule: "#0E2148", word: "#0E2148" },
      "mono-linen": { bg: "#0E2148", stroke: "#F4EEDC", accent: "#F4EEDC", rule: "#F4EEDC", word: "#F4EEDC" },
    }[palette] || {};

  // When the wordmark is on, use a wider 60×60 viewBox so 'Value Realisation'
  // has navy padding on either side. Mark content is offset by +6 to sit
  // symmetrically within the wider field.
  const vb = withWordmark ? { w: 60, h: 60, dx: 6 } : { w: 48, h: 48, dx: 0 };
  const height = withWordmark ? size : size;

  const dx = vb.dx;
  const cx = (x) => x + dx;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${vb.w} ${vb.h}`}
      width={size}
      height={height}
      role="img"
      aria-label={title}
      className={className}
    >
      <title>{title}</title>
      <rect width={vb.w} height={vb.h} fill={p.bg} />
      <rect x={cx(6)} y="7" width="10" height="1.6" fill={p.rule} />
      <line x1={cx(6)} y1="38" x2={cx(42)} y2="38" stroke={p.stroke} strokeOpacity="0.28" strokeWidth="0.8" />
      <line x1={cx(10)} y1="38" x2={cx(10)} y2="30" stroke={p.stroke} strokeWidth="2" strokeLinecap="square" />
      <line x1={cx(15)} y1="38" x2={cx(15)} y2="26" stroke={p.stroke} strokeWidth="2" strokeLinecap="square" />
      <line x1={cx(20)} y1="38" x2={cx(20)} y2="22" stroke={p.stroke} strokeWidth="2" strokeLinecap="square" />
      <line x1={cx(25)} y1="38" x2={cx(25)} y2="17" stroke={p.stroke} strokeWidth="2" strokeLinecap="square" />
      <line x1={cx(32)} y1="38" x2={cx(35)} y2="12" stroke={p.accent} strokeWidth="2.4" strokeLinecap="round" />
      <circle cx={cx(35)} cy="11" r="1.8" fill={p.accent} />
      {withWordmark && (
        <text
          x={vb.w / 2}
          y="52"
          textAnchor="middle"
          fill={p.word}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontStyle="italic"
          fontSize="6"
          letterSpacing="0.2"
        >
          Value Realisation
        </text>
      )}
    </svg>
  );
}
